import { useState, useRef, KeyboardEvent } from 'react';

export const DEFAULT_CARD_NUMBER = ['', '', '', ''];
export const CARD_SEGMENT_LENGTHS = [4, 4, 4, 4]; // 각 세그먼트별 최대 길이
export const LAST_SEGMENT_INDEX = 3; // 카드번호 마지막 세그먼트의 인덱스

export function useCardNumberSegments(
  initialSegments: string[] = DEFAULT_CARD_NUMBER
) {
  const [cardNumber, setCardNumber] = useState<string[]>(initialSegments);
  const [lastSegmentMaxLength, setLastSegmentMaxLength] = useState<number>(4);

  // Ref to track arrow key input
  const arrowKeyPressed = useRef<boolean>(false);
  // Refs to reference input elements
  const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);

  // Card number segment change handler (improved version)
  const handleSegmentChange = (rawValue: string, index: number): void => {
    // Ignore if arrow key was pressed or value hasn't changed
    if (arrowKeyPressed.current || rawValue === cardNumber[index]) {
      return;
    }

    const value = rawValue.replace(/[^0-9]/g, '');
    const maxLength = index === 3 ? lastSegmentMaxLength : 4;
    const newValue = value.slice(0, maxLength);

    // Check previous value (determine if editing or entering new value)
    const previousValue = cardNumber[index];
    const wasAlreadyFull = previousValue.length === maxLength;

    setCardNumber((prev: string[]): string[] => {
      const updated = [...prev];
      updated[index] = newValue;
      return updated;
    });

    // For the first segment, check for American Express
    if (index === 0) {
      setLastSegmentMaxLength(
        newValue.startsWith('34') || newValue.startsWith('37') ? 3 : 4
      );
    }

    // Auto focus move (only when entering new input, not when editing)
    if (newValue.length === maxLength && index < LAST_SEGMENT_INDEX && !wasAlreadyFull && !arrowKeyPressed.current) {
      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        setTimeout(() => nextInput.focus(), 0);
      }
    }
  };

  // Keyboard event handler
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number): void => {
    const isBackSpaceKeyDown = e.code === "Backspace";
    const isArrowKey = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key);
    const isTabKey = e.key === "Tab";
    const currentFieldLength = cardNumber[index].length;
    const maxFieldLength = index === 3 ? lastSegmentMaxLength : 4;
    const input = e.currentTarget;
    const cursorPosition = input.selectionStart || 0;

    // Handle arrow keys - prevent focus movement
    if (isArrowKey) {
      arrowKeyPressed.current = true;

      const isRightArrow = e.key === "ArrowRight";
      const isLeftArrow = e.key === "ArrowLeft";
      const atEnd = cursorPosition === currentFieldLength;
      const atStart = cursorPosition === 0;

      // Handle when field is full
      if (currentFieldLength === maxFieldLength) {
        // Right arrow at the end - prevent moving to next field
        if (isRightArrow && atEnd) {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            input.focus();
            input.setSelectionRange(currentFieldLength, currentFieldLength);
          }, 0);
          return;
        }

        // Left arrow at the start - prevent moving to previous field
        if (isLeftArrow && atStart) {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            input.focus();
            input.setSelectionRange(0, 0);
          }, 0);
          return;
        }
      }
      return;
    }

    // Allow default behavior for Tab key
    if (isTabKey) {
      return;
    }

    // Backspace on empty field - move to previous field
    if (currentFieldLength === 0 && isBackSpaceKeyDown && index > 0) {
      e.preventDefault();
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
        setTimeout(() => {
          const prevLength = cardNumber[index - 1].length;
          prevInput.setSelectionRange(prevLength, prevLength);
        }, 0);
      }
    }
  };

  // Keyboard key up event handler
  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
    const isArrowKey = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key);
    if (isArrowKey) {
      arrowKeyPressed.current = false;
    }
  };

  // Input ref setter function
  const setInputRef = (index: number) => (el: HTMLInputElement | null) => {
    inputRefs.current[index] = el;
  };

  return {
    cardNumber,
    lastSegmentMaxLength,
    handleSegmentChange,
    handleKeyDown,
    handleKeyUp,
    setInputRef,
    setCardNumber
  };
}
